import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type Promo, type PromoInput } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconReceipt } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 25;

/** Promo codes — the customer promo codes the rider app applies (incentives-svc
 *  owns the `promo_codes` table; the rider Promo screen reads the active ones via
 *  incentives-svc GET /v1/promos).
 *
 *  READ + AUTHORED here: staff with promos.manage can create / edit a promo and
 *  activate / deactivate it (admin-svc proxies the write to incentives-svc, which
 *  owns the table; audited + RBAC-gated server-side). Read-only roles still see
 *  the list. The section is DEFENSIVE (shows a "not yet available" state when the
 *  table is absent). Covers the Filament promo-code resource (list/view +
 *  create/edit + activate). */
export function Promos() {
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState('');
  // null = closed; 'new' = create; a Promo = edit that row.
  const [editing, setEditing] = useState<Promo | 'new' | null>(null);
  const canManage = useAdminPermission('promos.manage');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'promos', q, offset],
    queryFn: ({ signal }) => adminApi.promos({ q: q || undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Promo codes"
        subtitle="Customer promo codes applied at booking (owned by incentives-svc)."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search code / name…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Promo codes not yet in Postgres"
            description={
              data?.note ??
              'The promo_codes table is owned by incentives-svc and is not in this Postgres yet.'
            }
            icon={<IconReceipt className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {canManage ? (
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => setEditing('new')}
                className="btn-primary"
              >
                New promo
              </button>
            </div>
          ) : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading promo codes…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load promo codes" description={(error as Error)?.message} />
            ) : (
              <>
                <PromosTable rows={data?.items ?? []} canManage={canManage} onEdit={(p) => setEditing(p)} />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {editing ? (
        <PromoDrawer promo={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
    </AdminPage>
  );
}

/** The promo `promo_for` audience → a toned badge. */
const PROMO_FOR_TONE: Record<string, BadgeTone> = {
  ride: 'info',
  wallet: 'ok',
};

/** The promos DataTable. Pulled out so the page body stays readable. Includes an
 *  inline activate/deactivate toggle + an Edit button for promos.manage roles. */
function PromosTable({
  rows,
  canManage,
  onEdit,
}: {
  rows: Promo[];
  canManage: boolean;
  onEdit: (p: Promo) => void;
}) {
  const qc = useQueryClient();
  // The id currently being toggled (for the per-row pending state).
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const toggle = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 0 | 1 }) => adminApi.setPromoStatus(id, status),
    onMutate: ({ id }) => setTogglingId(id),
    onSettled: () => {
      setTogglingId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'promos'] });
    },
  });

  const columns: Array<Column<Promo>> = [
    { key: 'code', header: 'Code', cell: (r) => <span className="metric text-xs text-slate-300">{r.promo_code}</span> },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-slate-100">{r.promo_name}</span> },
    {
      key: 'for',
      header: 'For',
      cell: (r) => <Badge tone={PROMO_FOR_TONE[r.promo_for] ?? 'neutral'}>{r.promo_for || '—'}</Badge>,
    },
    {
      key: 'discount',
      header: 'Discount',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-ok">${r.discount.toFixed(2)}</span>,
    },
    {
      key: 'min_fare',
      header: 'Min fare',
      align: 'right',
      cell: (r) => <span className="metric text-xs text-slate-300">${r.min_fare.toFixed(2)}</span>,
    },
    {
      key: 'max',
      header: 'Max discount',
      align: 'right',
      cell: (r) => <span className="metric text-xs text-slate-300">${r.max_discount_value.toFixed(2)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'active' : 'inactive'}
        </Badge>
      ),
    },
  ];
  if (canManage) {
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={toggle.isPending && togglingId === r.id}
            onClick={() => toggle.mutate({ id: r.id, status: r.status === 1 ? 0 : 1 })}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-slate-300 hover:bg-ink-800 disabled:opacity-50"
          >
            {toggle.isPending && togglingId === r.id ? '…' : r.status === 1 ? 'Deactivate' : 'Activate'}
          </button>
          <button
            type="button"
            onClick={() => onEdit(r)}
            className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
          >
            Edit
          </button>
        </div>
      ),
    });
  }

  return (
    <DataTable<Promo>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={<EmptyState title="No promo codes" description="No promo codes yet." icon={<IconReceipt className="h-6 w-6" />} />}
    />
  );
}

// ─── promo authoring drawer ──────────────────────────────────

/** The audiences incentives-svc accepts. 'ride' discounts a trip fare; 'wallet'
 *  is a top-up bonus. Mirrors promo_codes.promo_for. */
const PROMO_FOR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'ride', label: 'Ride (discount a trip fare)' },
  { value: 'wallet', label: 'Wallet (top-up bonus)' },
];

/** The editable form state. Mirrors PromoInput but keeps the number fields as the
 *  strings the inputs bind to (parsed on submit). */
interface FormState {
  name: string;
  code: string;
  description: string;
  promo_for: string;
  discount: string;
  min_fare: string;
  max_discount_value: string;
  status: boolean; // true = active (1)
}

/** PromoDrawer — the create/edit form. promo=null ⇒ create; otherwise edit that
 *  row (seeded from it). On success it invalidates the promos list and closes.
 *
 *  RBAC: only ops + superadmin hold promos.manage; this drawer only ever opens
 *  for them (the page gates the New/Edit affordances on the same hook). admin-svc
 *  enforces the matrix server-side and 403s regardless. */
function PromoDrawer({ promo, onClose }: { promo: Promo | null; onClose: () => void }) {
  const isEdit = promo !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(() => seedForm(promo));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: PromoInput) => (isEdit ? adminApi.updatePromo(promo!.id, input) : adminApi.createPromo(input)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'promos'] });
      onClose();
    },
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  return (
    <Drawer
      title={isEdit ? `Edit promo #${promo!.id}` : 'New promo'}
      subtitle={isEdit ? promo!.promo_code : 'Author a customer promo code'}
      onClose={onClose}
    >
      <div className="space-y-4">
        {isEdit ? (
          <div>
            <Field label="ID" value={`#${promo!.id}`} />
            <Field label="Created" value={dateTime(promo!.created_at)} />
          </div>
        ) : null}

        <Card>
          <div className="space-y-3">
            <TextField
              label="Code"
              hint="The code riders enter (e.g. WELCOME10). Must be unique."
              value={form.code}
              onChange={(v) => set('code', v)}
              placeholder="WELCOME10"
            />
            <TextField
              label="Name"
              hint="Shown to riders on the Promo screen."
              value={form.name}
              onChange={(v) => set('name', v)}
              placeholder="Welcome Ride"
            />
            <div>
              <label className="block text-[11px] text-muted">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                rows={2}
                placeholder="Save on your first ride with Telride."
                className="mt-1 input"
              />
            </div>

            <div>
              <label className="block text-[11px] text-muted">Applies to</label>
              <select
                value={form.promo_for}
                onChange={(e) => set('promo_for', e.target.value)}
                className="mt-1 input"
              >
                {PROMO_FOR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Discount (AUD)"
                hint="The amount taken off."
                value={form.discount}
                onChange={(v) => set('discount', v)}
                type="number"
                placeholder="10.00"
              />
              <TextField
                label="Min fare (AUD)"
                hint="Minimum fare to qualify (0 = none)."
                value={form.min_fare}
                onChange={(v) => set('min_fare', v)}
                type="number"
                placeholder="0.00"
              />
            </div>

            <TextField
              label="Max discount (AUD)"
              hint="Cap on the discount value (0 = uncapped)."
              value={form.max_discount_value}
              onChange={(v) => set('max_discount_value', v)}
              type="number"
              placeholder="10.00"
            />

            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={form.status}
                onChange={(e) => set('status', e.target.checked)}
                className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500"
              />
              Active (riders can apply this code)
            </label>
          </div>
        </Card>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={onSubmit}
            className="btn-primary"
          >
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create promo'}
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          {clientError ? <span className="text-xs text-danger">{clientError}</span> : null}
          {mutation.isError ? <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span> : null}
        </div>
      </div>
    </Drawer>
  );
}

/** A labelled text/number input row used in the promo form. */
function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

/** Seed the form from a promo (edit) or empty defaults (create). */
function seedForm(p: Promo | null): FormState {
  if (!p) {
    return {
      name: '',
      code: '',
      description: '',
      promo_for: 'ride',
      discount: '',
      min_fare: '',
      max_discount_value: '',
      status: true,
    };
  }
  return {
    name: p.promo_name,
    code: p.promo_code,
    description: p.description ?? '',
    promo_for: p.promo_for || 'ride',
    discount: String(p.discount),
    min_fare: String(p.min_fare),
    max_discount_value: String(p.max_discount_value),
    status: p.status === 1,
  };
}

/** Validate + convert the form into a PromoInput, or return an error string.
 *  Mirrors admin-svc/incentives-svc validation so the staffer sees a precise
 *  message instantly; the server re-checks everything (this is a UX nicety, not
 *  the boundary). Empty number fields default to 0. */
function buildInput(f: FormState): { input: PromoInput } | { error: string } {
  const code = f.code.trim();
  if (!code) return { error: 'Code is required.' };
  const name = f.name.trim();
  if (!name) return { error: 'Name is required.' };

  const discount = numOrZero(f.discount);
  if (discount === null || discount < 0) return { error: 'Discount must be a number of zero or more.' };
  const minFare = numOrZero(f.min_fare);
  if (minFare === null || minFare < 0) return { error: 'Min fare must be a number of zero or more.' };
  const maxDiscount = numOrZero(f.max_discount_value);
  if (maxDiscount === null || maxDiscount < 0) return { error: 'Max discount must be a number of zero or more.' };

  return {
    input: {
      name,
      code,
      description: f.description.trim(),
      promo_for: f.promo_for || 'ride',
      discount,
      min_fare: minFare,
      max_discount_value: maxDiscount,
      status: f.status ? 1 : 0,
    },
  };
}

/** Parse a money input → number; '' → 0; an unparseable value → null (an error). */
function numOrZero(s: string): number | null {
  const t = s.trim();
  if (t === '') return 0;
  const n = Number(t);
  return Number.isNaN(n) ? null : n;
}
